import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Table, Button, Modal, Typography, Empty, Tag, message, Space, Popconfirm, List } from 'antd';
import { EyeOutlined, LockOutlined, UnlockOutlined, HistoryOutlined } from '@ant-design/icons';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import { useApp } from '../context/AppContext';
import { apiConfig, apiTokenRequest } from '../authConfig';
import { controlDocCode } from '../utils/controlImplementation';
import { stripCompanyPrefixFromDocumentTitle } from '../utils/documentDisplayName';
import { CiDocxEditorPanel } from '../components/ControlImplementation';
import {
    getDocumentLibraryDistributionState,
    isInternalAuditSignedOff,
    INTERNAL_AUDIT_SIGN_OFF_REQUIRED_MESSAGE,
} from '../utils/documentLibraryAccess';
import { formatDdMmYyyy } from '../utils/formatDdMmYyyy';

const { Title, Text } = Typography;

const FAMILY_TAG_CLASS = {
    Organizational: 'bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-hero)] border-[color:var(--color-border)]',
    People: 'bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]',
    Physical: 'bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]',
    Technological: 'bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]',
};

/**
 * Document Library — implemented ISMS documents after Control Implementation sign-off.
 *
 * Backlog #20 / Stage 10 "Approved document library" (workflow-doc slide 14; PRD §4.9):
 * distribution is roles-based and accessibility-oriented — Lead Advisor/platform_admin
 * publish or unpublish the library so the Company SPOC can review it. The UI gate below
 * is UX only; `POST .../approved-document-library/publish-state` re-checks the role
 * server-side (Commandment III/V — never rely on hiding the button alone).
 */
const DocumentLibraryView = () => {
    const { activeProject, user, fetchProjectProfile } = useApp();
    const { instance } = useMsal();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewRow, setPreviewRow] = useState(null);
    const [publishBusy, setPublishBusy] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const companyDisplayName = activeProject?.companyName || activeProject?.name || '';

    // See utils/documentLibraryAccess.js — extracted so the gate is unit-testable without
    // mounting this component's antd / AppContext / MSAL surface.
    const pkg = activeProject?.profile?.approvedDocumentLibraryPackage || null;
    const {
        isPublished,
        visibleToRoles,
        historyEntries,
        canManagePublishState,
        canToggleDistribution,
        isCompanySpoc,
        spocCanSeeLibrary,
    } = getDocumentLibraryDistributionState(user, pkg);
    // Stage 10 needs the Stage 9 SPOC sign-off. Only a publish is gated; unpublish stays available.
    const auditSignedOff = isInternalAuditSignedOff(activeProject?.profile);
    const publishLocked = !isPublished && !auditSignedOff;

    const getApiToken = useCallback(async () => {
        const resp = await instance.acquireTokenSilent({
            ...apiTokenRequest,
            account: instance.getActiveAccount(),
        });
        return resp.accessToken;
    }, [instance]);

    const fetchLibrary = useCallback(async () => {
        if (!activeProject?.id) return;
        // Don't fetch document rows the SPOC isn't allowed to see yet.
        if (isCompanySpoc && !spocCanSeeLibrary) return;
        setLoading(true);
        try {
            const token = await getApiToken();
            const res = await fetch(
                `${apiConfig.baseUrl}/api/v1/projects/${activeProject.id}/document-library`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                message.error(json.error?.message || 'Could not load document library.');
                setRows([]);
                return;
            }
            setRows(Array.isArray(json.data?.rows) ? json.data.rows : []);
        } catch (err) {
            message.error(err?.message || 'Could not load document library.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [activeProject?.id, isCompanySpoc, spocCanSeeLibrary, getApiToken]);

    useEffect(() => {
        void fetchLibrary();
    }, [fetchLibrary]);

    // The Stage 9 SPOC sign-off happens in another session; re-read the profile on open so the Stage 10
    // publish lock reflects the stored stamp rather than a stale in-memory copy.
    useEffect(() => {
        if (canManagePublishState && activeProject?.id && fetchProjectProfile) void fetchProjectProfile(activeProject.id);
    }, [activeProject?.id, canManagePublishState]);

    const handleTogglePublish = useCallback(async () => {
        if (!activeProject?.id || !canToggleDistribution) return;
        const nextAction = isPublished ? 'unpublish' : 'publish';
        setPublishBusy(true);
        try {
            const token = await getApiToken();
            const res = await fetch(
                `${apiConfig.baseUrl}/api/v1/projects/${activeProject.id}/approved-document-library/publish-state`,
                {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(
                        nextAction === 'publish'
                            ? { action: 'publish', visibleToRoles: ['company_spoc'] }
                            : { action: 'unpublish' },
                    ),
                },
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                // Surfaces the API's own sentence, incl. 403 STAGE_PREREQUISITE_NOT_MET (Stage 9 sign-off missing).
                message.error(json.error?.message || `Could not ${nextAction} the document library.`);
                return;
            }
            message.success(
                nextAction === 'publish'
                    ? 'Document library published - the Company SPOC can now review it.'
                    : 'Document library unpublished - the Company SPOC can no longer see it.',
            );
            if (fetchProjectProfile) await fetchProjectProfile(activeProject.id);
        } catch (err) {
            message.error(err?.message || 'Could not update the publish state.');
        } finally {
            setPublishBusy(false);
        }
    }, [activeProject?.id, canToggleDistribution, isPublished, getApiToken, fetchProjectProfile]);

    const columns = useMemo(
        () => [
            {
                title: 'Document Name',
                key: 'documentName',
                ellipsis: true,
                render: (_, row) => (
                    <div>
                        <div className="font-semibold text-[color:var(--color-text-hero)]">
                            {stripCompanyPrefixFromDocumentTitle(row.documentName, companyDisplayName) || 'Untitled'}
                        </div>
                        <Text type="secondary" className="font-mono text-xs">
                            {controlDocCode(row.controlId)}
                        </Text>
                    </div>
                ),
            },
            {
                title: 'Control',
                dataIndex: 'controlRef',
                key: 'controlRef',
                width: 100,
            },
            {
                title: 'Family',
                key: 'family',
                width: 140,
                render: (_, row) => {
                    const v = row.family;
                    if (!v) return <Text type="secondary">-</Text>;
                    return (
                        <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                FAMILY_TAG_CLASS[v] || 'bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]'
                            }`}
                        >
                            {v}
                        </span>
                    );
                },
            },
            {
                title: 'Status',
                key: 'status',
                width: 120,
                render: () => <Tag color="success">Implemented</Tag>,
            },
            {
                title: 'Implemented Date',
                key: 'implementedAt',
                width: 160,
                render: (_, row) => formatDdMmYyyy(row.implementedAt),
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 100,
                render: (_, row) => (
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        disabled={!row.brandedBlobUrl}
                        onClick={() => setPreviewRow(row)}
                    >
                        View
                    </Button>
                ),
            },
        ],
        [companyDisplayName],
    );

    if (!activeProject) {
        return <PageState status="empty" message="No project selected" />;
    }

    // Company SPOC view (backlog #20): the advisor controls accessibility via publish/
    // unpublish — until published for this role, show a pending state, never the content.
    if (isCompanySpoc && !spocCanSeeLibrary) {
        return (
            <PageShell>
                <PageHeader title="Document Library" subtitle={`Implemented ISMS documents for ${activeProject.name}`} />
                <PageState status="empty" message="Your advisor hasn't published the Approved Document Library yet. Check back once it's published." />
            </PageShell>
        );
    }

    return (
        <PageShell>
            <PageHeader title="Document Library" subtitle={`Implemented ISMS documents for ${activeProject.name}`} />

            {canManagePublishState && (
                <div
                    style={{
                        marginBottom: 24,
                        padding: 16,
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-surface-sunken)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 12,
                        }}
                    >
                        <div>
                            <Space size={8} align="center">
                                <Text strong>Distribution status:</Text>
                                {isPublished ? (
                                    <Tag color="success" icon={<UnlockOutlined />}>Published</Tag>
                                ) : (
                                    <Tag color="default" icon={<LockOutlined />}>Not published</Tag>
                                )}
                            </Space>
                            <div style={{ marginTop: 4 }}>
                                {isPublished ? (
                                    <Text type="secondary">
                                        Published on {formatDdMmYyyy(pkg?.publishedAt)}
                                        {pkg?.publishedBy ? ` by ${pkg.publishedBy}` : ''}
                                        {visibleToRoles.length ? ` - visible to: ${visibleToRoles.join(', ')}` : ''}
                                    </Text>
                                ) : (
                                    <Text type="secondary">
                                        The Company SPOC cannot see this library until it is published.
                                    </Text>
                                )}
                                {publishLocked && canToggleDistribution && (
                                    <div data-testid="publish-locked-reason">
                                        <Text type="warning">{INTERNAL_AUDIT_SIGN_OFF_REQUIRED_MESSAGE}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                        <Space>
                            {historyEntries.length > 0 && (
                                <Button
                                    size="small"
                                    icon={<HistoryOutlined />}
                                    onClick={() => setHistoryOpen((v) => !v)}
                                >
                                    {historyOpen ? 'Hide history' : `History (${historyEntries.length})`}
                                </Button>
                            )}
                            {canToggleDistribution ? (
                            <Popconfirm
                                title={isPublished ? 'Unpublish document library?' : 'Publish document library?'}
                                description={
                                    isPublished
                                        ? 'The Company SPOC will no longer be able to view these documents.'
                                        : 'The Company SPOC will be able to view these documents.'
                                }
                                okText={isPublished ? 'Unpublish' : 'Publish'}
                                onConfirm={handleTogglePublish}
                                disabled={publishBusy || publishLocked}
                            >
                                <Button
                                    type={isPublished ? 'default' : 'primary'}
                                    danger={isPublished}
                                    loading={publishBusy}
                                    disabled={publishLocked}
                                    title={publishLocked ? INTERNAL_AUDIT_SIGN_OFF_REQUIRED_MESSAGE : undefined}
                                >
                                    {isPublished ? 'Unpublish' : 'Publish'}
                                </Button>
                            </Popconfirm>
                            ) : (
                                <Text type="secondary">Only the Lead Advisor can publish this library.</Text>
                            )}
                        </Space>
                    </div>

                    {historyOpen && historyEntries.length > 0 && (
                        <List
                            size="small"
                            style={{ marginTop: 12 }}
                            dataSource={historyEntries}
                            renderItem={(h, idx) => (
                                <List.Item key={h.ts ? `${h.ts}-${idx}` : idx}>
                                    <Text>
                                        <Tag color={h.action === 'published' ? 'success' : 'default'}>
                                            {h.action || 'unknown'}
                                        </Tag>{' '}
                                        by {h.actorRole || 'unknown'} on {formatDdMmYyyy(h.ts)}
                                    </Text>
                                </List.Item>
                            )}
                        />
                    )}
                </div>
            )}

            <Title level={5} style={{ marginBottom: 16, fontWeight: 800 }}>
                Implemented ISMS Documents
            </Title>

            {rows.length === 0 && !loading ? (
                <Empty
                    description="No implemented documents yet. Complete Control Implementation and submit each document at the Implemented stage."
                />
            ) : (
                <Table
                    rowKey="rowKey"
                    loading={loading}
                    dataSource={rows}
                    columns={columns}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    scroll={{ x: 900 }}
                />
            )}

            <Modal
                open={!!previewRow}
                onCancel={() => setPreviewRow(null)}
                title={
                    previewRow
                        ? stripCompanyPrefixFromDocumentTitle(previewRow.documentName, companyDisplayName) || 'Document'
                        : 'Document'
                }
                footer={null}
                width={1000}
                destroyOnClose
            >
                {previewRow?.brandedBlobUrl ? (
                    <CiDocxEditorPanel
                        projectId={activeProject.id}
                        controlId={previewRow.controlId}
                        brandedBlobUrl={previewRow.brandedBlobUrl}
                        draftHtml={previewRow.draftHtml || ''}
                        readOnly
                        getApiToken={getApiToken}
                        onSaveDraft={null}
                        onRefresh={fetchLibrary}
                    />
                ) : null}
            </Modal>
        </PageShell>
    );
};

export default DocumentLibraryView;
